import { Navigate, useNavigate } from 'react-router';
import {
    RequestMethod,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { useState } from 'react';
import {
    AuthedValidationContent,
    AuthValidationErrorReason,
    AuthValidationSuccess,
    AuthValidationSuccessResponse,
    LinkLoginData,
    LinkLoginPopup,
    ValidationOrigin,
    ValidationState,
} from './ParkingaboSharedRegistrationValidationRoute.tsx';
import { LoggedInRouteCompProps } from '../RouteUtils.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { useParamOperatorExternalId } from '../../components/CustomerProvider.tsx';

export function AuthedOperatorRegistrationValidationRoute({
    logout,
    linkedUsers,
    refetchUsers,
}: LoggedInRouteCompProps) {
    const operatorExternalId = useParamOperatorExternalId();
    const navigate = useNavigate();
    const [validationState, setValidationState] = useState<ValidationState>(
        ValidationState.LOADING,
    );
    const [linkLoginData, setLinkLoginData] = useState<LinkLoginData | null>(
        null,
    );

    const [customerValidationState] = useServerFetch<
        AuthValidationSuccess,
        { operatorExternalId: string },
        { reason: AuthValidationErrorReason }
    >(
        ({ operatorExternalId }) => ({
            url: `/ui-api/parkingabo/user/operator/${operatorExternalId}/validation`,
        }),
        operatorExternalId ? { operatorExternalId: operatorExternalId } : null,
    );

    const [createAndLinkCustomerToLoginState, createAndLinkCustomerToLogin] =
        useServerWrite<{ operatorExternalId: string }, object, ValidationData>(
            () => ({
                url: `/ui-api/parkingabo/user/operator/${operatorExternalId}/link-login`,
                method: RequestMethod.POST,
            }),
        );

    useServerSuccessEffect(customerValidationState, data => {
        switch (data.response) {
            case AuthValidationSuccessResponse.LINK_LOGIN:
                setLinkLoginData({
                    email: data.email,
                    operatorName: data.operatorName,
                });
                break;
            case AuthValidationSuccessResponse.LOGGED_IN:
                navigate(`/${data.operatorExternalId}`);
                break;
        }
    });

    useServerErrorEffect(customerValidationState, (_, data) => {
        if (data === null) {
            setValidationState(ValidationState.GENERIC_ERROR);
        } else {
            switch (data.reason) {
                case AuthValidationErrorReason.EMAIL_NOT_VALID:
                    setValidationState(ValidationState.EMAIL_NOT_VALID);
                    break;
                case AuthValidationErrorReason.ALREADY_REGISTERED_ON_OPERATOR:
                    setValidationState(
                        ValidationState.ALREADY_REGISTERED_ON_OPERATOR,
                    );
                    break;
            }
        }
    });

    if (operatorExternalId === null) {
        return <Navigate to="/" replace />;
    }

    if (linkLoginData !== null) {
        return (
            <LinkLoginPopup
                linkLoginData={linkLoginData}
                onConfirm={() =>
                    createAndLinkCustomerToLogin({
                        operatorExternalId: operatorExternalId,
                    })
                }
                linkCustomerToLoginState={createAndLinkCustomerToLoginState}
                onAbort={() => logout()}
                linkedUsers={linkedUsers}
                refetchUsers={refetchUsers}
            />
        );
    }

    return (
        <AuthedValidationContent
            validationState={validationState}
            origin={ValidationOrigin.OPERATOR}
        />
    );
}
